import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { MAX_LOGGED_STRING_LENGTH } from 'src/constants/logging';
import { isObject } from '../../utils';

@Injectable({
  providedIn: 'root'
})
export class LogService {
  private log = console.log;
  private debug = environment.debug;

  constructor() {}

  public primary(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #cce5ff; color: #004085');
    }
  }

  public secondary(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #e2e3e5; color: #383d41');
    }
  }

  public success(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #d4edda; color: #155724');
    }
  }

  public error(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #f8d7da; color: #721c24');
    }
  }

  public warning(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #fff3cd; color: #856404');
    }
  }

  public info(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #d1ecf1; color: #0c5460');
    }
  }

  public light(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #fefefe; color: #818182');
    }
  }

  public dark(tag: string, message: any): void {
    if (this.debug) {
      this.log(`%c # ${tag} \n ${message} `, 'background: #d6d8d9; color: #1b1e21');
    }
  }

  /**
   * Logs an object, truncating long strings first.
   * Photo payloads are multi megabyte base64 strings, dumping those in full floods
   * the native console and makes the surrounding log entries unreadable.
   */
  public object(object: any): void {
    if (this.debug) {
      this.log(this.truncateLongStrings(object));
    }
  }

  public table(object: any): void {
    if (this.debug) {
      console.table(object);
    }
  }

  /**
   * Returns a copy of the given value with every long string shortened.
   * Walks arrays and plain objects, anything else is returned untouched.
   */
  private truncateLongStrings(value: any): any {
    if (typeof value === 'string') {
      return value.length > MAX_LOGGED_STRING_LENGTH
        ? `${value.slice(0, MAX_LOGGED_STRING_LENGTH)}... [${value.length} chars truncated]`
        : value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.truncateLongStrings(item));
    }

    // Errors and class instances are left alone, only plain objects are rebuilt
    if (isObject(value) && Object.getPrototypeOf(value) === Object.prototype) {
      return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, this.truncateLongStrings(item)]));
    }

    return value;
  }
}
